/**
 * Filing Status Strip — home-dashboard widget showing GSTR-1 / 2B / 3B
 * side-by-side with last filed period + next due date + days remaining.
 * Fires a red/amber/green pill per return so accountants never miss a cycle.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FileCheck, Repeat, Sparkles, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const STATUS_TONE = {
  filed: "border-emerald-500 bg-emerald-500/10 text-emerald-500",
  ready: "border-emerald-500 bg-emerald-500/10 text-emerald-500",
  upcoming: "border-sky-500 bg-sky-500/10 text-sky-500",
  overdue: "border-red-500 bg-red-500/10 text-red-500",
};

const STATUS_LABEL = {
  filed: "Filed", ready: "Ready", upcoming: "Upcoming", overdue: "Overdue",
};

function _pretty(period) {
  if (!period) return "—";
  const m = parseInt(period.slice(0, 2), 10);
  const y = period.slice(2);
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[m - 1]} ${y}`;
}

function _dueBadge(days) {
  if (days == null) return null;
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Due today";
  if (days <= 7) return `${days}d left`;
  return `${days}d left`;
}

const ReturnCard = ({ icon: Icon, label, period, dueDate, arn, status,
                        daysRemaining, cta, dueLabel = "Due" }) => (
  <div className={`border-l-4 rounded-sm p-3 ${STATUS_TONE[status] || "border-white/10 bg-white/5"}`}
       data-testid={`filing-card-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] font-mono uppercase tracking-widest">{label}</div>
      </div>
      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-black/20">
        {STATUS_LABEL[status] || "—"}
      </span>
    </div>
    <div className="mt-2 space-y-0.5">
      <div className="text-xs text-muted-foreground">Last {label === "GSTR-2B" ? "available" : "filed"}</div>
      <div className="text-sm font-mono">{_pretty(period)}</div>
      {arn && (<div className="text-[10px] font-mono text-muted-foreground truncate" title={arn}>ARN {arn}</div>)}
    </div>
    <div className="mt-2 flex items-baseline justify-between border-t border-white/10 pt-1">
      <div>
        <div className="text-[10px] text-muted-foreground">{dueLabel} {dueDate}</div>
        <div className={`text-xs font-bold ${daysRemaining < 0 ? "text-red-400" :
                                                 daysRemaining <= 3 ? "text-amber-400" : "text-white/80"}`}>
          {_dueBadge(daysRemaining)}
        </div>
      </div>
      {cta && (
        <Link to={cta.href} className="text-[10px] font-mono uppercase tracking-widest underline hover:no-underline">
          {cta.label} →
        </Link>
      )}
    </div>
  </div>
);

export default function FilingStatusStrip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/filing-status").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="filing-status-strip">
      <ReturnCard icon={Sparkles} label="GSTR-1"
        period={data.gstr1.last_filed_period}
        dueDate={data.gstr1.next_due_date}
        arn={data.gstr1.last_arn}
        status={data.gstr1.status}
        daysRemaining={data.gstr1.days_remaining}
        cta={{ href: "/app/gstr1", label: "3-way reco" }} />
      <ReturnCard icon={Repeat} label="GSTR-2B"
        period={data.gstr2b.last_available_period}
        dueDate={data.gstr2b.next_available_date}
        status={data.gstr2b.status}
        daysRemaining={data.gstr2b.days_remaining}
        dueLabel="Available"
        cta={{ href: "/app/ims", label: "IMS dashboard" }} />
      <ReturnCard icon={FileCheck} label="GSTR-3B"
        period={data.gstr3b.last_filed_period}
        dueDate={data.gstr3b.next_due_date}
        arn={data.gstr3b.last_arn}
        status={data.gstr3b.status}
        daysRemaining={data.gstr3b.days_remaining}
        cta={{ href: "/app/gstr3b", label: "Prepare 3B" }} />
    </div>
  );
}
